import React, { useCallback, useEffect, useMemo, useState } from "react";
import type {
  NormalizedRecommendation,
  NormalizedSeoFinding,
  OperatorAuditDetailV1,
  OperatorRecommendationRow,
  SeoAuditRunId,
  SiteId,
} from "@siteagain/shared-types";

import type { Locale } from "../i18n";
import type { OperatorCopy } from "./copy";
import { formatDateTime, operatorFetch, relativeTime } from "./format";
import { AlertIcon, ArrowIcon, RefreshIcon } from "./icons";
import type { RecommendationAction } from "./PipelineBoard";
import { SiteMark } from "./SiteMark";

const SEVERITY_ORDER = ["critical", "warning", "opportunity", "info"] as const;
const IMPACT_ORDER = { high: 0, medium: 1, low: 2 } as const;
const OPEN_STATUSES = new Set(["new", "viewed", "saved"]);
const LIVE_STATUSES = new Set(["applied", "measuring", "measured"]);
const KEYWORD_ROWS = 12;

/**
 * One audit, read as a page: where the site stands, what to do about it, and the evidence behind
 * it. The steps it raised can be acted on here; everything else stays a plain read of the stored
 * report. A run that is still going says so and refreshes itself until the report lands.
 */
export function AuditPage({
  auditId,
  copy,
  locale,
  now,
  version,
  busyKey,
  onBack,
  onOpenSite,
  onRunAudit,
  onRecommendation,
}: {
  auditId: SeoAuditRunId;
  copy: OperatorCopy;
  locale: Locale;
  now: number;
  /** Bumped after every action, so a decision shows here without reopening the page. */
  version: number;
  busyKey: string;
  onBack(): void;
  onOpenSite(siteId: SiteId): void;
  onRunAudit(siteId: SiteId, busyKey: string): void;
  onRecommendation(id: string, action: RecommendationAction, busyKey: string): void;
}) {
  const t = copy.audit;
  const [detail, setDetail] = useState<OperatorAuditDetailV1 | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const response = await operatorFetch<{ detail: OperatorAuditDetailV1 }>(`/operator/audits/${auditId}`);
      setDetail(response.detail);
      setError("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t.loadFailed);
    }
  }, [auditId, t.loadFailed]);

  useEffect(() => { setDetail(null); window.scrollTo({ top: 0 }); }, [auditId]);
  useEffect(() => { void load(); }, [load, version]);

  const pending = detail?.run.status === "queued" || detail?.run.status === "running";
  useEffect(() => {
    if (!pending) return;
    const timer = window.setInterval(() => { if (document.visibilityState === "visible") void load(); }, 8_000);
    return () => window.clearInterval(timer);
  }, [pending, load]);

  const run = detail?.run;
  const report = run?.report ?? null;
  const auditKey = detail ? `site:${detail.site.id}:audit` : "";

  return (
    <div className="mc-audit">
      <nav className="mc-crumbs" aria-label={t.eyebrow}>
        <button type="button" className="mc-back" onClick={onBack}><ArrowIcon />{t.back}</button>
        {detail && (
          <button type="button" className="mc-site" onClick={() => onOpenSite(detail.site.id)}>
            <SiteMark siteId={detail.site.id} name={detail.site.name} />{detail.site.name}
          </button>
        )}
      </nav>

      {error && <p className="mc-inline-error" role="alert">{error}</p>}
      {!detail && !error && <div className="mc-skeleton" aria-hidden="true"><i /><i /><i /><i /></div>}

      {detail && run && (
        <>
          <header className="mc-audit-hero">
            <div className="mc-audit-title">
              <small>{t.eyebrow} · {t.purpose[run.purpose] ?? run.purpose}</small>
              <h1>{detail.site.productionDomain ?? hostOf(run.sourceUrl)}</h1>
              <p>
                {report ? t.captured(formatDateTime(report.capturedAt, locale)) : relativeTime(run.createdAt, locale, now)}
                {" · "}<a className="mc-link" href={run.sourceUrl} target="_blank" rel="noreferrer">{run.sourceUrl}</a>
              </p>
            </div>
            {report && <ScoreDial score={report.summary.score} grade={report.summary.grade} copy={copy} />}
            <div className="mc-audit-hero-actions">
              <button
                type="button"
                className="mc-button-secondary"
                disabled={busyKey === auditKey || pending}
                onClick={() => onRunAudit(detail.site.id, auditKey)}
              >
                <RefreshIcon />{busyKey === auditKey ? copy.action.working : t.runAgain}
              </button>
            </div>
          </header>

          {pending && (
            <section className="mc-audit-state" role="status">
              <span className="mc-pulse" aria-hidden="true" />
              <div><strong>{t.running}</strong><p>{t.runningBody}</p></div>
            </section>
          )}
          {run.status === "failed" && (
            <section className="mc-audit-state is-error" role="alert">
              <AlertIcon />
              <div><strong>{t.failed}</strong><p>{errorText(run.error) ?? t.failedBody}</p></div>
            </section>
          )}
          {report?.partial && <p className="mc-note" role="note"><AlertIcon />{t.partial}</p>}

          {report && (
            <div className="mc-audit-grid">
              <div className="mc-audit-main">
                <NextSteps
                  rows={detail.recommendations}
                  report={report.recommendations}
                  copy={copy}
                  busyKey={busyKey}
                  onRecommendation={onRecommendation}
                />
                <Findings findings={report.findings} copy={copy} />
                <Keywords rows={report.metrics.queryMetrics} copy={copy} locale={locale} />
              </div>
              <aside className="mc-audit-side">
                <Components scores={report.metrics.componentScores} copy={copy} />
                <Visibility report={report} copy={copy} locale={locale} />
                {report.limitations.length > 0 && (
                  <section className="mc-audit-card">
                    <h2>{t.limitations}</h2>
                    <ul className="mc-plain-list">{report.limitations.map((item) => <li key={item}>{item}</li>)}</ul>
                  </section>
                )}
              </aside>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ScoreDial({ score, grade, copy }: { score: number | null; grade: string | null; copy: OperatorCopy }) {
  if (score === null) return null;
  const value = Math.max(0, Math.min(100, Math.round(score)));
  const tone = value >= 80 ? "ok" : value >= 50 ? "wait" : "error";
  return (
    <div className={`mc-dial tone-${tone}`} role="img" aria-label={`${value} ${copy.audit.outOf}`}>
      <svg viewBox="0 0 120 120" aria-hidden="true">
        <circle cx="60" cy="60" r="52" className="track" />
        <circle cx="60" cy="60" r="52" className="value" pathLength={100} strokeDasharray={`${value} 100`} />
      </svg>
      <span><strong>{value}</strong><small>{grade ? copy.audit.grade(grade) : copy.audit.outOf}</small></span>
    </div>
  );
}

/**
 * The steps the audit raised, most useful first, with their live state: a step already taken
 * shows where it went instead of offering the same button twice.
 */
function NextSteps({
  rows,
  report,
  copy,
  busyKey,
  onRecommendation,
}: {
  rows: OperatorRecommendationRow[];
  report: NormalizedRecommendation[];
  copy: OperatorCopy;
  busyKey: string;
  onRecommendation(id: string, action: RecommendationAction, busyKey: string): void;
}) {
  const t = copy.audit;
  const explained = useMemo(() => new Map(report.map((item) => [item.title, item])), [report]);
  const sorted = [...rows].sort((left, right) =>
    Number(!OPEN_STATUSES.has(left.status)) - Number(!OPEN_STATUSES.has(right.status))
    || IMPACT_ORDER[left.impactBand] - IMPACT_ORDER[right.impactBand]);
  return (
    <section className="mc-audit-section">
      <header><h2>{t.todo}</h2><p>{t.todoHint}</p></header>
      {sorted.length === 0 ? <p className="mc-empty-line">{t.todoEmpty}</p> : (
        <ol className="mc-steps">
          {sorted.map((row) => {
            const key = (action: string) => `recommendation:${row.id}:${action}`;
            const busy = busyKey.startsWith(`recommendation:${row.id}:`);
            const open = OPEN_STATUSES.has(row.status);
            const why = explained.get(row.title);
            return (
              <li key={row.id} className={open ? "" : LIVE_STATUSES.has(row.status) ? "is-done" : "is-moving"}>
                <div className="mc-step-head">
                  <span className={`mc-impact impact-${row.impactBand}`}>{t.impact[row.impactBand]}</span>
                  <span className="mc-step-type">{copy.drawer.type[row.type]}</span>
                  {!open && <span className="mc-chip">{copy.drawer.recommendationStatus[row.status]}</span>}
                </div>
                <h3>{row.title}</h3>
                {why?.explanation && <p>{why.explanation}</p>}
                {why?.proposedAction && open && <p className="mc-step-action"><ArrowIcon />{why.proposedAction}</p>}
                {open && (
                  <div className="mc-actions">
                    <button type="button" className="primary" disabled={busy} onClick={() => onRecommendation(row.id, "preview", key("preview"))}>
                      {busyKey === key("preview") ? copy.action.working : copy.action.buildPreview}
                    </button>
                    {row.status !== "saved" && (
                      <button type="button" disabled={busy} onClick={() => onRecommendation(row.id, "save", key("save"))}>{copy.action.save}</button>
                    )}
                    <button type="button" className="mc-quiet" disabled={busy} onClick={() => onRecommendation(row.id, "skip", key("skip"))}>{copy.action.dismiss}</button>
                    <span className={`mc-chip safety-${row.safetyClass}`}>{copy.pipeline.safety[row.safetyClass]}</span>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

/** Findings grouped by how much they matter; the fix is one click away rather than always open. */
function Findings({ findings, copy }: { findings: NormalizedSeoFinding[]; copy: OperatorCopy }) {
  const t = copy.audit;
  const groups = SEVERITY_ORDER
    .map((severity) => ({ severity, items: findings.filter((item) => item.severity === severity) }))
    .filter((group) => group.items.length > 0);
  return (
    <section className="mc-audit-section">
      <header><h2>{t.findings}</h2></header>
      {groups.length === 0 ? <p className="mc-empty-line">{t.findingsEmpty}</p> : groups.map((group) => (
        <div key={group.severity} className={`mc-finding-group severity-${group.severity}`}>
          <h3><i aria-hidden="true" />{t.severity[group.severity]} <span>{group.items.length}</span></h3>
          {group.items.map((finding) => (
            <details key={finding.providerKey} className="mc-finding">
              <summary>
                <strong>{finding.title}</strong>
                {finding.affectedUrls.length > 0 && <small>{t.affected(finding.affectedUrls.length)}</small>}
              </summary>
              <div>
                {finding.explanation && <p>{finding.explanation}</p>}
                {finding.remediation && <><h4>{t.remediation}</h4><p>{finding.remediation}</p></>}
                {finding.affectedUrls.length > 0 && (
                  <ul className="mc-plain-list mc-urls">{finding.affectedUrls.slice(0, 8).map((url) => <li key={url}>{url}</li>)}</ul>
                )}
              </div>
            </details>
          ))}
        </div>
      ))}
    </section>
  );
}

function Keywords({
  rows,
  copy,
  locale,
}: {
  rows: Array<{ query: string; position: number | null; impressions: number | null; clicks: number | null; searchVolume?: number | null }>;
  copy: OperatorCopy;
  locale: Locale;
}) {
  const t = copy.audit;
  const [all, setAll] = useState(false);
  if (rows.length === 0) return null;
  const number = new Intl.NumberFormat(locale === "fr" ? "fr-FR" : "en-GB");
  const show = (value: number | null | undefined) => (value === null || value === undefined ? "—" : number.format(Math.round(value * 10) / 10));
  const visible = all ? rows : rows.slice(0, KEYWORD_ROWS);
  return (
    <section className="mc-audit-section">
      <header><h2>{t.keywords}</h2><p>{t.keywordsHint}</p></header>
      <div className="mc-table-wrap">
        <table className="mc-table mc-keywords">
          <thead>
            <tr>
              <th>{t.columns.query}</th><th>{t.columns.position}</th><th>{t.columns.impressions}</th><th>{t.columns.clicks}</th><th>{t.columns.volume}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.query}>
                <td>{row.query}</td>
                <td className="mc-num">{show(row.position)}</td>
                <td className="mc-num">{show(row.impressions)}</td>
                <td className="mc-num">{show(row.clicks)}</td>
                <td className="mc-num">{show(row.searchVolume)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > KEYWORD_ROWS && (
        <button type="button" className="mc-link-button mc-more" onClick={() => setAll((value) => !value)}>
          {all ? copy.recap.showLess : copy.today.showAll(rows.length)}
        </button>
      )}
    </section>
  );
}

function Components({ scores, copy }: { scores: Record<string, number>; copy: OperatorCopy }) {
  const entries = Object.entries(scores).filter(([, value]) => Number.isFinite(value));
  if (entries.length === 0) return null;
  return (
    <section className="mc-audit-card">
      <h2>{copy.audit.components}</h2>
      <ul className="mc-components">
        {entries.map(([name, value]) => {
          const rounded = Math.max(0, Math.min(100, Math.round(value)));
          return (
            <li key={name}>
              <span>{humanizeKey(name)}</span>
              <strong>{rounded}</strong>
              <i aria-hidden="true"><b className={rounded >= 80 ? "ok" : rounded >= 50 ? "wait" : "error"} style={{ width: `${rounded}%` }} /></i>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Only the figures the provider actually returned; a missing figure is left out, never zero. */
function Visibility({ report, copy, locale }: { report: NonNullable<OperatorAuditDetailV1["run"]["report"]>; copy: OperatorCopy; locale: Locale }) {
  const t = copy.audit;
  const insights = report.tregInsights;
  const number = new Intl.NumberFormat(locale === "fr" ? "fr-FR" : "en-GB");
  const facts: Array<[string, string]> = [];
  if (insights?.aiCitationSharePercent != null) facts.push([t.aiCitation, `${Math.round(insights.aiCitationSharePercent)} %`]);
  if (insights?.llmMentions) facts.push([t.mentions, number.format(insights.llmMentions.totalMentions)]);
  if (insights?.backlinkSummary?.referringDomains != null) facts.push([t.referringDomains, number.format(insights.backlinkSummary.referringDomains)]);
  if (insights?.backlinkSummary?.domainAuthority != null) facts.push([t.authority, String(Math.round(insights.backlinkSummary.domainAuthority))]);
  if (insights?.domainKeywords) facts.push([t.rankedKeywords, number.format(insights.domainKeywords.totalRankedKeywords)]);
  if (facts.length === 0) return null;
  return (
    <section className="mc-audit-card">
      <h2>{t.visibility}</h2>
      <dl className="mc-visibility">
        {facts.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
      </dl>
    </section>
  );
}

function errorText(error: Record<string, unknown> | null): string | null {
  if (!error) return null;
  return typeof error.message === "string" ? error.message : typeof error.reason === "string" ? error.reason : null;
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./u, "");
  } catch {
    return url;
  }
}

function humanizeKey(value: string): string {
  const spaced = value.replace(/[_-]+/gu, " ").replace(/([a-z])([A-Z])/gu, "$1 $2").trim();
  const sentence = spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
  return sentence.replace(/\b(ai|seo|geo|ctr|ux)\b/giu, (word) => word.toUpperCase());
}
